import Vue from 'vue';
import _ from 'lodash';
import { ResponsiveUtils } from '../../utils';

type StickyPosition = 'top' | 'bottom' | 'none';

export type Position = {
	sm: StickyPosition;
	md: StickyPosition;
	lg: StickyPosition;
	[index: string]: string;
};

type StickyParams = {
	/**
	 * Amount of offset, after which the sticky should appear.
	 * Default: 150 px
	 */
	offset?: number | 'vh';
	/**
	 * When this flag is set, the sticky will permanently visible.
	 */
	fixed: boolean;
	/**
	 * Positions where the sticky should appear on the different viewports.
	 * Top, bottom or none for each mobile, tablet and desktop viewport.
	 */
	positions?: Position;
};

const DEFAULT_OFFSET = 150;
const DEFAULT_POSITIONS: Position = { sm: 'none', md: 'none', lg: 'top' };

function determineOffset(value?: StickyParams) {
	if (value?.fixed) return 0;
	if (value?.offset === 'vh') return window.innerHeight;
	if (value?.offset === undefined) return DEFAULT_OFFSET;
	return value.offset;
}

function determinePositions(value?: StickyParams) {
	if (value?.positions) {
		return value.positions;
	}
	return DEFAULT_POSITIONS;
}

function scrollEvent(el: HTMLElement, value?: StickyParams) {
	const offset = determineOffset(value);
	const positions = determinePositions(value);

	const respUtils = new ResponsiveUtils();
	const vp = respUtils.getViewport();
	const position = positions[vp];

	switch (position) {
		case 'none':
			el.classList.remove('top-0');
			el.classList.remove('bottom-0');
			el.classList.remove('olb-sticky--bottom');
			return;
		case 'top':
			el.classList.remove('olb-sticky--bottom');
			el.classList.remove('bottom-0');
			window.scrollY >= offset ? el.classList.add('top-0') : el.classList.remove('top-0');
			return;
		case 'bottom':
			el.classList.add('olb-sticky--bottom');
			el.classList.remove('top-0');
			window.scrollY >= offset ? el.classList.add('bottom-0') : el.classList.remove('bottom-0');
			return;
	}
}

export default Vue.directive('sticky', {
	inserted: function(el: HTMLElement) {
		el.classList.add('olb-sticky');
	},
	bind: function(el: HTMLElement, { value }: { value?: StickyParams }) {
		// next tick necessary because e.g. the set positions
		// in sticky.vue's onMounted would not be applied yet
		Vue.nextTick(() => {
			scrollEvent(el, value);
		});
		value &&
			document.addEventListener(
				'scroll',
				_.throttle(() => {
					scrollEvent(el, value);
				}, 200),
			);
	},
});
